import React, { useEffect, useState } from 'react';
import { ModalsKeys } from 'global';
import { logError } from 'utils/logger';
import { useSelector } from 'react-redux';
import { Box, Button } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { isNullOrUndefined } from 'utils/typeGuargs';
import { zodResolver } from '@hookform/resolvers/zod';
import { DecisionCard } from 'modules/creditSubmission';
import { generateEquipmentName } from 'modules/equipment';
import { ModalsContainer } from 'components/shared/Modals';
import { useHistory, useLocation } from 'react-router-dom';
import { creditAppSelectors } from 'redux/creditAppReducer';
import { CreateProposalDTO, useProposal } from 'modules/proposals';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { DeskingEquipmentFormSection, FinanceStructure, DeskingSaleDetailsFormSection } from 'modules/desking/shared';

import { lenderDeskingApi } from '../api';
import { AddContingencyDialog } from './dialogs';
import { lenderDeskingDataShema } from '../model';
import { lenderDeskingPanelMapper } from '../mappers';
import { StyledLenderDeskingPanel } from './StyledLenderDeskingPanel';
import { useCreditSubmission } from '../../hooks/useCreditSubmission';
import { LenderDeskingPanelFormValues, LenderDeskingPanelProps, LenderDeskingSections } from '../types';
import {
  ActionsSection,
  CustomerSection,
  ContingenciesSection,
  PaymentOptionsSection,
  LenderDescriptionSection,
  ProposalConfigurationSection,
} from './sections';

import { collectFinanceVariablesForCalc } from '../../../desking/lib';
import { LenderDeskingPanelApiContainer } from './LenderDeskingPanelApiContainer';
import { RenderWithCondition } from '../../../../components/shared/Conditional/RenderWithCondition';
import { decisionCodeToStatus } from '../../constants';

const initialSectionView = Object.values(LenderDeskingSections).reduce(
  (acc, section) => {
    acc[section] = { isSectionOpen: true, isSectionCollapsed: true };
    return acc;
  },
  {} as Record<LenderDeskingSections, { isSectionOpen: boolean; isSectionCollapsed: boolean }>
);

const modalsWithKeys = {
  [ModalsKeys.AddContingencyDialog]: <AddContingencyDialog />,
};

export const LenderDeskingPanel = ({
  cs,
  voId,
  invoice,
  equipment,
  accountId,
  dealerEmail,
  creditAppData,
  currentUserEmail,
}: LenderDeskingPanelProps) => {
  const { replace } = useHistory();
  const location = useLocation();

  const [onCalculate, { loading: isCalculating, previousData }] = useLazyQuery(lenderDeskingApi.calculate);

  const { handleCreateProposalOnSubmissionApproval, isProposalCreating } = useProposal();
  const { handlePostCSDecision } = useCreditSubmission();

  const contactSelectOptions = useSelector(creditAppSelectors.contactSelectOptions);

  const [sectionView, setSectionView] = useState(initialSectionView);
  const [selectedCSDecision, setSelectedCSDecision] = useState<number>(
    decisionCodeToStatus[cs.decision as keyof typeof decisionCodeToStatus]
  );
  const [selectedTerm, setSelectedTerm] = useState<string | null>(null);
  const [financePricing, setFinancePricing] = useState<Record<string, any>>({});

  const form = useForm<LenderDeskingPanelFormValues>({
    mode: 'all',
    resolver: zodResolver(lenderDeskingDataShema),
    defaultValues: lenderDeskingPanelMapper.initPanel(
      invoice,
      creditAppData,
      equipment,
      cs?.approvedAmount ?? 0,
      cs?.creditSubmissionId
    ),
  });

  const [program, saleDetails] = useWatch({ control: form.control, name: ['financeQuote', 'saleDetails'] });

  useEffect(() => {
    const isSkipCalculate = !program.amount || !program.terms.length;

    if (isSkipCalculate) return;

    // TODO create own mapper
    onCalculate({
      variables: collectFinanceVariablesForCalc({
        program: program as any,
        saleDetails,
        proposalMenu: null,
        financeProgramDescription: '',
        isExcludeEquipmentSales: false,
        proposalProductConfigurationsInMenu: {},
        submission: { selectedSubmissionId: cs?.creditSubmissionId, maxApprovalAmount: cs?.approvedAmount ?? 0 },
      }),
    })
      .then(response => {
        const optionsData = response?.data?.calculateMultiplePayments?.[0]?.paymentOptions;
        const prevOptionsData = previousData?.calculateMultiplePayments?.[0]?.paymentOptions;
        setFinancePricing(normalizePaymentsByTerm(optionsData ?? prevOptionsData));
      })
      .catch(logError);
  }, [cs, program, onCalculate, saleDetails, previousData?.calculateMultiplePayments]);

  const handleCreateProposalAndUpdateCS = async (
    proposalDTO: Omit<CreateProposalDTO, 'vendorOpportunityId' | 'creditSubmissionId'>,
    csDTO: Record<string, any>
  ) => {
    const isCSDecisionApproved = selectedCSDecision === 803370003;

    if (!isCSDecisionApproved) {
      await handlePostCSDecision(cs?.creditSubId, { decision: selectedCSDecision });
    } else {
      await handlePostCSDecision(cs?.creditSubId, {
        decision: selectedCSDecision,
        masterCreditSubmissionId: cs.masterCreditSubmissionId ?? null,
        ...csDTO,
      });

      await handleCreateProposalOnSubmissionApproval({
        vendorOpportunityId: voId,
        creditSubmissionId: cs?.creditSubmissionId,
        ...proposalDTO,
      });

      replace({ hash: '#proposals', pathname: location.pathname });
    }
  };

  const handleSaveAsDraft = async (formValues: LenderDeskingPanelFormValues) => {
    const createAsDraftDTO = lenderDeskingPanelMapper.processFormToCreateDto({
      formValues,
      isDraft: true,
      financePricing,
    });

    await handleCreateProposalAndUpdateCS(createAsDraftDTO, {
      rate: formValues.financeQuote.commonRate,
      approvedAmount: formValues.financeQuote.maxApprovalAmount,
    });
  };

  const handleSendToApplicant = async (formValues: LenderDeskingPanelFormValues) => {
    const ccEmails: Partial<Record<'lender' | 'dealer', string>> = {
      lender: currentUserEmail,
      ...(formValues.actions.ccToDealer && dealerEmail ? { dealer: dealerEmail } : {}),
    };

    const createSendToApplicantDTO = lenderDeskingPanelMapper.processFormToCreateDto({
      ccEmails,
      formValues,
      isDraft: false,
      financePricing,
    });

    await handleCreateProposalAndUpdateCS(createSendToApplicantDTO, {
      rate: formValues.financeQuote.commonRate,
      approvedAmount: formValues.financeQuote.maxApprovalAmount,
    });
  };

  const handleResetStructure = () => {
    setFinancePricing({});
    form.reset();
  };

  return (
    <>
      <LenderDeskingPanelApiContainer accountId={accountId}>
        {({ loading, financeProgramOptions, actions }) => (
          <StyledLenderDeskingPanel>
            <FormProvider {...form}>
              <Box className="row">
                <CustomerSection
                  isSectionDisabled
                  name="Customer"
                  customers={contactSelectOptions}
                  value={LenderDeskingSections.CUSTOMER}
                />

                <DeskingSaleDetailsFormSection
                  isSupportSaleDetailsUpdating={false}
                  value={LenderDeskingSections.SALE_DETAILS}
                />
              </Box>

              <DeskingEquipmentFormSection
                name="Equipment"
                isSectionDisabled
                equipments={equipment}
                value={LenderDeskingSections.EQUIPMENT}
                isSectionOpen={sectionView[LenderDeskingSections.EQUIPMENT].isSectionCollapsed}
                handleUpdateOpenStatus={(section, nextValue) => {
                  setSectionView(prev => ({ ...prev, [section]: { ...prev[section], isSectionCollapsed: nextValue } }));
                }}
                equipmentOptions={equipment.map(equipmentItem => ({
                  value: equipmentItem.equipmentId,
                  label: generateEquipmentName(equipmentItem),
                }))}
              />

              <DecisionCard
                cs={cs}
                onDecisionChanged={nextValue => {
                  setSelectedCSDecision(nextValue.decision);
                  form.setValue('financeQuote.commonRate', nextValue.rate);
                  form.setValue('financeQuote.maxApprovalAmount', nextValue.approvedAmount);
                }}
              />

              <RenderWithCondition condition={selectedCSDecision === 803370003}>
                <Box className="row">
                  <FinanceStructure
                    name="Structure"
                    financeAmount={invoice.invoiceTotal}
                    value={LenderDeskingSections.STRUCTURE}
                    financeProgramOptions={financeProgramOptions}
                    isFPOptionsLoading={loading.isFPOptionsLoading}
                    maxApprovalAmount={program.maxApprovalAmount ?? 0}
                    onFetchFinanceProgram={async programId =>
                      actions.handleFetchFinanceProgram(programId, cs?.scoreCardResult)
                    }
                    netInvoiceAmount={Number(form.getValues('equipment.totalWholesaleCost'))}
                    extraAction={
                      <Button size="small" onClick={handleResetStructure}>
                        Reset
                      </Button>
                    }
                  />

                  <PaymentOptionsSection
                    selectedTerm={selectedTerm}
                    isCalculating={isCalculating}
                    financePricing={financePricing}
                    onSelectOption={term => setSelectedTerm(prev => (prev === term ? null : term))}
                  />
                </Box>
              </RenderWithCondition>

              <LenderDescriptionSection />

              <Box className="row">
                <ProposalConfigurationSection
                  isSectionDisabled
                  name="Proposal Configuration"
                  value={LenderDeskingSections.PROPOSAL_CONFIGURATION}
                />

                <ContingenciesSection
                  isSectionDisabled
                  name="Contingencies"
                  value={LenderDeskingSections.CONTINGENCIES}
                />
              </Box>

              <ActionsSection
                isSectionDisabled
                onSaveAsDraft={form.handleSubmit(handleSaveAsDraft)}
                onSendToApplicant={form.handleSubmit(handleSendToApplicant)}
                isProposalProcessing={form.formState.isSubmitting || isProposalCreating}
              />
            </FormProvider>
          </StyledLenderDeskingPanel>
        )}
      </LenderDeskingPanelApiContainer>

      <ModalsContainer modalsWithKeys={modalsWithKeys} />
    </>
  );
};

function normalizePaymentsByTerm(options: any[] | null | undefined): Record<string, any> {
  if (isNullOrUndefined(options)) return {};

  return options.reduce((acc, payment) => {
    acc[payment.term] = payment;
    return acc;
  }, {});
}
